// Rides and ride requests. Desktop: filters in a left sidebar and a list of
// rows grouped by day. Phones: the app layout, cards with the filters tucked
// behind a button. Booking opens BookingSheet.

import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBlockedUsers } from '../safety';
import { COMMON, SHORT, departure, localDay, matchesPlace, displayPlace } from '../places';
import { SponsorLogo, sponsorRideLabel, useCurrentBreak, useSponsorTheme, useTopDrivers } from '../liveData';
import { useNames, initialsOf } from '../userNames';
import { SafetyMenu } from '../Safety/Safety';
import BookingSheet from './BookingSheet';
import AvatarPhoto from '../AvatarPhoto';
import './Rides.css';

/* ── Dates (the ride's real departure, in Mountain Time) ─────── */
const MT = 'America/Denver';
const dayLabel  = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'short', month: 'short', day: 'numeric' });
const longLabel = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'long', month: 'long', day: 'numeric' });
const clock     = new Intl.DateTimeFormat('en-US', { timeZone: MT, hour: 'numeric', minute: '2-digit' });
const hourOf    = new Intl.DateTimeFormat('en-US', { timeZone: MT, hour: 'numeric', hourCycle: 'h23' });
const weekdayOf = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'short' });
const isToday    = d => localDay(d) === localDay(new Date());
const isTomorrow = d => localDay(d) === localDay(new Date(Date.now() + 86400000));
const isWeekend  = d => ['Sat', 'Sun'].includes(weekdayOf.format(d));

/** Listed until 2 hours after they leave (same rule as iOS). */
const PAST_GRACE_MS = 2 * 60 * 60 * 1000;
const seatsOf = p => parseInt(p.seats, 10) || 0;

function upcoming(value) {
  const cutoff = Date.now() - PAST_GRACE_MS;
  return Object.entries(value || {})
    .map(([id, v]) => ({ id, ...v, _dep: departure(v) }))
    .filter(p => p._dep && p._dep.getTime() > cutoff);
}

/**
 * Day sections in list order ("Today", "Tomorrow", "Wednesday, November 25").
 * Busy days (6+ posts) split into Morning / Afternoon / Evening.
 */
function makeSections(list) {
  const days = [];
  for (const item of list) {
    const key = localDay(item._dep);
    const last = days[days.length - 1];
    const title = isToday(item._dep) ? 'Today' : isTomorrow(item._dep) ? 'Tomorrow' : longLabel.format(item._dep);
    if (last && last.key === key) last.items.push(item);
    else days.push({ key, title, items: [item] });
  }
  const sections = [];
  for (const day of days) {
    if (day.items.length < 6) { sections.push({ id: day.key, title: day.title, items: day.items }); continue; }
    for (const item of day.items) {
      const h = +hourOf.format(item._dep);
      const part = h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening';
      const last = sections[sections.length - 1];
      if (last && last.id === `${day.key}-${part}`) last.items.push(item);
      else sections.push({ id: `${day.key}-${part}`, title: day.title, sub: part, items: [item] });
    }
  }
  return sections;
}

/* ── Icons ───────────────────────────────────────────────────── */
const Arrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const Plus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const Sliders = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="4" y1="7" x2="20" y2="7"/><line x1="4" y1="17" x2="20" y2="17"/><circle cx="9" cy="7" r="2.5" fill="var(--surface)"/><circle cx="15" cy="17" r="2.5" fill="var(--surface)"/>
  </svg>
);

/* ── One ride / request ──────────────────────────────────────── */
function RideItem({ item, isRide, name, topDriver, mine, booked, showDay, onOpen }) {
  const sponsorTheme = useSponsorTheme();
  const seats = seatsOf(item);
  const donation = item.donationRequested ? String(item.donationRequested).replace(/^\$/, '').trim() : '';
  const status = isRide
    ? (seats > 0 ? `${seats} seat${seats !== 1 ? 's' : ''} left` : 'Full')
    : (seats > 0 ? `Needs ${seats} seat${seats !== 1 ? 's' : ''}` : 'Has a driver');
  const canAct = !mine && !booked && seats > 0;

  return (
    <article className={`ride-item${isRide ? '' : ' request'}${canAct ? '' : ' muted'}${item.sponsor ? ' sponsored' : ''}`}>
      {item.sponsor && <span className="ride-item-sponsor">{sponsorRideLabel(sponsorTheme, item.sponsor)}</span>}
      <button className="ride-item-main" onClick={canAct ? onOpen : undefined} disabled={!canAct}
        aria-label={`${displayPlace(item.fromLocation)} to ${displayPlace(item.toLocation)}, ${clock.format(item._dep)}, ${status}`}>
        <span className="ride-item-time">{clock.format(item._dep)}</span>
        <span className="ride-item-route">
          <span>{displayPlace(item.fromLocation)}</span><Arrow /><span>{displayPlace(item.toLocation)}</span>
        </span>
        {showDay && <span className="ride-item-day">{dayLabel.format(item._dep)}</span>}
        {item.additionalInfo && <span className="ride-item-note">{item.additionalInfo}</span>}
      </button>

      {item.sponsor ? (
        // Sponsored rides show the sponsor where the driver usually is
        // (the driver is on the booking sheet), like the app.
        <span className="ride-item-person ride-item-offered">
          <span className="ride-item-offered-label">Offered by</span>
          <SponsorLogo name={item.sponsor} />
        </span>
      ) : (
      <span className="ride-item-person">
        <span className="ride-item-avatar">{initialsOf(name)}<AvatarPhoto uid={item.userID} /></span>
        <span className="ride-item-name">
          {mine ? 'You' : name || '…'}
          {isRide && topDriver && <span className="top-driver-star" title="Top driver: 5+ booked rides this year" aria-label="Top driver">★</span>}
        </span>
        {!mine && item.userID && (
          <SafetyMenu target={{ userID: item.userID, name, contextType: isRide ? 'ride' : 'request', contextId: item.id }} />
        )}
      </span>
      )}

      <span className="ride-item-meta">
        <span className={`ride-item-seats${seats > 0 ? '' : ' none'}`}>{status}</span>
        {item.sponsor ? <span className="ride-item-donation free">FREE</span>
          : donation && <span className="ride-item-donation">${donation}{isRide ? '' : ' offered'}</span>}
      </span>

      <span className="ride-item-action">
        {mine ? <span className="ride-item-mine">{isRide ? 'Your ride' : 'Your request'}</span>
          : booked ? <span className="ride-item-booked">✓ {isRide ? 'Booked' : 'Accepted'}</span>
          : canAct ? <button className="ride-item-btn" onClick={onOpen}>{isRide ? 'Book' : 'Offer a ride'}</button>
          : null}
      </span>
    </article>
  );
}

/* ── Page ────────────────────────────────────────────────────── */
function Rides() {
  // Home links here with { mode, tab, book } to open a list, the break, or one ride.
  const location = useLocation();
  const linked   = location.state || {};
  const navigate = useNavigate();
  const db = getDatabase();
  const me = getAuth().currentUser?.uid;

  const [mode, setMode]         = useState(linked.mode === 'requests' ? 'requests' : 'rides');
  const [rides, setRides]       = useState(null); // null while loading
  const [requests, setRequests] = useState(null);

  const [search,       setSearch]       = useState('');
  const [when,         setWhen]         = useState(linked.tab === 'break' ? 'break' : 'all');
  const [sortBy,       setSortBy]       = useState('soonest');
  const [openOnly,     setOpenOnly]     = useState(true);
  const [fromFilter,   setFromFilter]   = useState('');
  const [toFilter,     setToFilter]     = useState('');
  const [filtersOpen,  setFiltersOpen]  = useState(false); // phones

  const [sheet, setSheet] = useState(null); // { item, kind }
  const [bookedIds, setBookedIds] = useState(() => new Set()); // posts you already booked or accepted
  const [pendingBook, setPendingBook] = useState(linked.book || null);

  const blocked      = useBlockedUsers();
  const currentBreak = useCurrentBreak();
  const topDrivers   = useTopDrivers();

  useEffect(() => {
    const stopRides = onValue(ref(db, 'rideReserve'), s => setRides(upcoming(s.val())), () => setRides([]));
    const stopReqs  = onValue(ref(db, 'rideRequest'), s => setRequests(upcoming(s.val())), () => setRequests([]));
    const stopBooked = me ? onValue(query(ref(db, 'bookings'), orderByChild('bookerUserID'), equalTo(me)),
      s => { const ids = new Set(); s.forEach(c => { ids.add(c.val().rideID); }); setBookedIds(ids); }, () => {}) : () => {};
    return () => { stopRides(); stopReqs(); stopBooked(); };
  }, [db, me]);

  useEffect(() => {
    if (location.state) navigate(location.pathname, { replace: true, state: null }); // not again on refresh
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Blocked people's posts are hidden everywhere on this page.
  const visibleRides    = useMemo(() => (rides || []).filter(r => !blocked.has(r.userID)), [rides, blocked]);
  // Requests whose rider already booked a ride for them are sorted (their
  // own copy, with "Close request", stays in Profile).
  const visibleRequests = useMemo(() => (requests || []).filter(r => !blocked.has(r.userID) && (!r.bookedRide || r.userID === me)), [requests, blocked, me]);
  const isRide  = mode === 'rides';
  const loading = isRide ? rides === null : requests === null;
  const current = isRide ? visibleRides : visibleRequests;
  const names   = useNames(current.map(p => p.userID));

  // Opened from a Home row: show that ride's booking once it's loaded.
  useEffect(() => {
    if (!pendingBook || rides === null) return;
    const ride = rides.find(r => r.id === pendingBook);
    if (ride && ride.userID !== me && seatsOf(ride) > 0) setSheet({ item: ride, kind: 'ride' });
    setPendingBook(null);
  }, [pendingBook, rides, me]);

  const whenTab = when === 'break' && !currentBreak ? 'all' : when; // the break tab ends with the break

  const filtered = useMemo(() => {
    let out = current;
    if (openOnly) out = out.filter(r => seatsOf(r) > 0);
    if (whenTab === 'today')    out = out.filter(r => isToday(r._dep));
    if (whenTab === 'tomorrow') out = out.filter(r => isTomorrow(r._dep));
    if (whenTab === 'weekend')  out = out.filter(r => isWeekend(r._dep));
    if (whenTab === 'break')    out = out.filter(r => currentBreak.contains(r));
    // "USAFA" also covers Up Top and the gates; "DEN" matches "dia", etc.
    if (fromFilter) out = out.filter(r => matchesPlace(fromFilter, r.fromLocation || ''));
    if (toFilter)   out = out.filter(r => matchesPlace(toFilter, r.toLocation || ''));
    const q = search.trim();
    if (q) {
      out = out.filter(r =>
        matchesPlace(q, r.fromLocation || '') || matchesPlace(q, r.toLocation || '') ||
        (names[r.userID] || '').toLowerCase().includes(q.toLowerCase()));
    }
    return [...out].sort((a, b) => (sortBy === 'seats' ? seatsOf(b) - seatsOf(a) : a._dep - b._dep));
  }, [current, openOnly, whenTab, currentBreak, fromFilter, toFilter, search, names, sortBy]);

  // Sponsored free rides with a seat left are pinned above everything else
  // (they still respect the filters); once full they drop back into place.
  const sections = useMemo(() => {
    const featured = isRide ? filtered.filter(r => r.sponsor && seatsOf(r) > 0) : [];
    const rest = featured.length ? filtered.filter(r => !featured.includes(r)) : filtered;
    const main = sortBy === 'soonest' ? makeSections(rest) : [{ id: 'all', title: null, items: rest }];
    return featured.length ? [{ id: 'featured', title: 'Featured', featured: true, items: featured }, ...main] : main;
  }, [filtered, sortBy, isRide]);

  const breakRides  = currentBreak ? visibleRides.filter(r => currentBreak.contains(r)).length : 0;
  const breakAsking = currentBreak ? new Set(visibleRequests.filter(r => currentBreak.contains(r) && !r.bookedRide).map(r => r.userID)).size : 0;
  const placeOptions = COMMON.map(p => ({ value: p, label: SHORT[p] || p }));
  const whenOptions = [
    { key: 'all', label: 'All upcoming' },
    ...(currentBreak ? [{ key: 'break', label: `${currentBreak.icon} ${currentBreak.name}` }] : []),
    { key: 'today', label: 'Today' },
    { key: 'tomorrow', label: 'Tomorrow' },
    { key: 'weekend', label: 'This weekend' },
  ];
  const activeFilters = (fromFilter ? 1 : 0) + (toFilter ? 1 : 0) + (sortBy !== 'soonest' ? 1 : 0) + (openOnly ? 0 : 1);
  const clearAll = () => { setFromFilter(''); setToFilter(''); setSortBy('soonest'); setOpenOnly(true); setWhen('all'); setSearch(''); };

  const filters = (
    <>
      <div className="rides-filter-group">
        <span className="rides-filter-label">When</span>
        <div className="rides-when" role="radiogroup" aria-label="When">
          {whenOptions.map(o => (
            <button key={o.key} role="radio" aria-checked={whenTab === o.key}
              className={`rides-when-btn${whenTab === o.key ? ' active' : ''}`} onClick={() => setWhen(o.key)}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
      <label className="rides-filter-group">
        <span className="rides-filter-label">Leaving from</span>
        <select className={`rides-select${fromFilter ? ' set' : ''}`} value={fromFilter} onChange={e => setFromFilter(e.target.value)}>
          <option value="">Anywhere</option>
          {placeOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </label>
      <label className="rides-filter-group">
        <span className="rides-filter-label">Going to</span>
        <select className={`rides-select${toFilter ? ' set' : ''}`} value={toFilter} onChange={e => setToFilter(e.target.value)}>
          <option value="">Anywhere</option>
          {placeOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </label>
      <label className="rides-filter-group">
        <span className="rides-filter-label">Sort</span>
        <select className="rides-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="soonest">Soonest first</option>
          <option value="seats">{isRide ? 'Most seats left' : 'Most seats needed'}</option>
        </select>
      </label>
      <label className="rides-check">
        <input type="checkbox" checked={openOnly} onChange={e => setOpenOnly(e.target.checked)} />
        {isRide ? 'Only rides with seats left' : 'Only requests that need a driver'}
      </label>
      {(activeFilters > 0 || whenTab !== 'all' || search) && (
        <button className="rides-clear" onClick={clearAll}>Clear filters</button>
      )}
    </>
  );

  return (
    <div className={`rides-page ${isRide ? 'mode-rides' : 'mode-requests'}`}>
      <aside className="rides-sidebar" aria-label="Filters">
        <div className="rides-mode" role="tablist" aria-label="Show">
          <button role="tab" aria-selected={isRide} className={isRide ? 'active' : ''} onClick={() => setMode('rides')}>
            Rides <span>{visibleRides.length}</span>
          </button>
          <button role="tab" aria-selected={!isRide} className={!isRide ? 'active req' : ''} onClick={() => setMode('requests')}>
            Requests <span>{visibleRequests.length}</span>
          </button>
        </div>
        {filters}
      </aside>

      <main className="rides-main">
        <header className="rides-head">
          <div>
            <h1>{isRide ? 'Find a ride' : 'People asking for rides'}</h1>
            <p>{loading ? ' ' : `${filtered.length} ${isRide ? 'ride' : 'request'}${filtered.length !== 1 ? 's' : ''}${whenTab === 'break' ? ` for ${currentBreak.name}` : ''}`}</p>
          </div>
          <div className="rides-head-actions">
            <button className="rides-btn ghost" onClick={() => navigate('/addrequest')}><Plus /> Ask for a ride</button>
            <button className="rides-btn primary" onClick={() => navigate('/addreserve')}><Plus /> Post a ride</button>
          </div>
        </header>

        {/* Phones: mode switch, search and a Filters button */}
        <div className="rides-mobile-bar">
          <div className="rides-mode" role="tablist" aria-label="Show">
            <button role="tab" aria-selected={isRide} className={isRide ? 'active' : ''} onClick={() => setMode('rides')}>Rides</button>
            <button role="tab" aria-selected={!isRide} className={!isRide ? 'active req' : ''} onClick={() => setMode('requests')}>Requests</button>
          </div>
        </div>

        <div className="rides-searchbar">
          <label className="rides-search">
            <SearchIcon />
            <input type="search" placeholder="Search a place or name" value={search} onChange={e => setSearch(e.target.value)} />
          </label>
          <button className={`rides-filters-btn${filtersOpen ? ' open' : ''}`} onClick={() => setFiltersOpen(v => !v)} aria-expanded={filtersOpen}>
            <Sliders /> Filters{activeFilters + (whenTab !== 'all' ? 1 : 0) > 0 && <span>{activeFilters + (whenTab !== 'all' ? 1 : 0)}</span>}
          </button>
        </div>
        {filtersOpen && <div className="rides-mobile-filters">{filters}</div>}

        {currentBreak && whenTab !== 'break' && (
          <button className="rides-break" style={{ background: currentBreak.wash, '--accent': currentBreak.accent }}
            onClick={() => { setWhen('break'); if (breakRides === 0 && breakAsking > 0) setMode('requests'); }}>
            <span className="rides-break-icon" aria-hidden="true">{currentBreak.icon}</span>
            <span className="rides-break-text">
              <strong>{currentBreak.name} <em>{currentBreak.range}</em></strong>
              <span>{breakRides} ride{breakRides !== 1 ? 's' : ''} · {breakAsking} asking · {currentBreak.tagline}</span>
            </span>
            <span className="rides-break-cta">Show</span>
          </button>
        )}

        {loading ? (
          <div className="rides-sections">
            {[0, 1, 2, 3].map(i => <div key={i} className="ride-item skeleton"><span /><span /></div>)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rides-empty">
            <strong>No {isRide ? 'rides' : 'requests'} match</strong>
            <span>{isRide ? 'Try other filters, or ask for a ride and drivers going your way will see it.'
              : 'Nobody is asking for that right now.'}</span>
            <div className="rides-empty-actions">
              {(activeFilters > 0 || whenTab !== 'all' || search) && <button className="rides-btn ghost" onClick={clearAll}>Clear filters</button>}
              {isRide && <button className="rides-btn primary" onClick={() => navigate('/addrequest')}>Ask for a ride</button>}
            </div>
          </div>
        ) : (
          <div className="rides-sections">
            {sections.map(section => (
              <section key={section.id} className="rides-section" aria-label={section.title || 'Results'}>
                {section.title && (
                  <h2 className="rides-section-title">{section.title}{section.sub && <span> · {section.sub}</span>}</h2>
                )}
                <div className="rides-list">
                  {section.items.map(item => (
                    <RideItem key={item.id} item={item} isRide={isRide} name={names[item.userID]}
                      topDriver={topDrivers[item.userID]} mine={item.userID === me}
                      booked={bookedIds.has(item.id)} showDay={sortBy !== 'soonest' || section.featured}
                      onOpen={() => setSheet({ item, kind: isRide ? 'ride' : 'request' })} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {sheet && (
        <BookingSheet item={sheet.item} kind={sheet.kind} name={names[sheet.item.userID]}
          topDriver={topDrivers[sheet.item.userID]} onClose={() => setSheet(null)} />
      )}
    </div>
  );
}

export default Rides;
